<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { parseDate } from '@/lib/parseDate';

const auth = useAuthStore();

// PayPal Client ID (Live)
const PAYPAL_CLIENT_ID = 'ATIG7uvqo2VsVRa8NEM0GIriThae9ZNjTn2HOz9T-L5MMTO1RYXKohBTMBKfkiGgGJJ_aYPim8ypHikx';

// Steps: 'email' | 'login' | 'register' | 'payment' | 'processing' | 'success' | 'error'
const step = ref('email');

// Form data
const email = ref('');
const password = ref('');
const firstName = ref('');
const lastName = ref('');
const agbAccepted = ref(false);
const privacyAccepted = ref(false);

// Errors
const formError = ref('');

// PayPal
const paypalLoaded = ref(false);
const paypalButtonRendered = ref(false);
const accessEndDate = ref('');
const justPurchased = ref(false);
const showPaypalButtons = ref(false);

// Price
const MIN_PRICE = 24;
const PRESET_AMOUNTS = [24, 36, 50, 100];
const selectedAmount = ref(24);
const customAmount = ref('');
const showCustomInput = ref(false);

// Computed: Actual payment amount
const paymentAmount = computed(() => {
  if (showCustomInput.value && customAmount.value) {
    const amount = parseInt(customAmount.value, 10);
    return amount >= MIN_PRICE ? amount : MIN_PRICE;
  }
  return selectedAmount.value;
});

// Select preset amount
const selectAmount = (amount) => {
  selectedAmount.value = amount;
  showCustomInput.value = false;
  customAmount.value = '';
};

// Show custom amount input
const enableCustomAmount = () => {
  showCustomInput.value = true;
  customAmount.value = '';
};

// Computed: Access end date (1 year from today)
const computedAccessEndDate = computed(() => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
});

// Load PayPal SDK
const loadPayPalSDK = () => {
  return new Promise((resolve, reject) => {
    if (window.paypal) {
      resolve(window.paypal);
      return;
    }

    const existingScript = document.getElementById('paypal-sdk');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.paypal));
      return;
    }

    const script = document.createElement('script');
    script.id = 'paypal-sdk';
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=EUR&locale=de_DE`;
    script.onload = () => {
      paypalLoaded.value = true;
      resolve(window.paypal);
    };
    script.onerror = () => reject(new Error('PayPal SDK could not be loaded'));
    document.head.appendChild(script);
  });
};

// Render PayPal Button
const renderPayPalButton = async () => {
  if (paypalButtonRendered.value) return;

  try {
    const paypal = await loadPayPalSDK();

    await nextTick();

    const container = document.getElementById('paypal-button-container');
    if (!container) return;

    // Clear container
    container.innerHTML = '';

    paypal.Buttons({
      style: {
        layout: 'vertical',
        color: 'gold',
        shape: 'rect',
        label: 'paypal'
      },

      // Create order on PayPal
      createOrder: async () => {
        formError.value = '';

        const result = await auth.createPayPalOrder(paymentAmount.value);

        if (!result.ok) {
          formError.value = result.error || 'Fehler beim Erstellen der Bestellung';
          throw new Error(result.error);
        }

        return result.orderId;
      },

      // Capture payment after approval
      onApprove: async (data) => {
        step.value = 'processing';
        formError.value = '';

        const result = await auth.capturePayPalOrder(data.orderID);

        if (result.ok) {
          accessEndDate.value = result.access?.ends_at
            ? parseDate(result.access.ends_at).toLocaleDateString('de-DE')
            : computedAccessEndDate.value;
          justPurchased.value = true;
          step.value = 'success';
        } else {
          formError.value = result.error || 'Zahlung konnte nicht abgeschlossen werden';
          step.value = 'error';
        }
      },

      onCancel: () => {
        formError.value = 'Zahlung wurde abgebrochen.';
      },

      onError: (err) => {
        console.error('PayPal Error:', err);
        formError.value = 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
      }
    }).render('#paypal-button-container');

    paypalButtonRendered.value = true;
  } catch (err) {
    console.error('Error rendering PayPal button:', err);
    formError.value = 'PayPal konnte nicht geladen werden.';
  }
};

// Watch for showPaypalButtons to render PayPal button
watch(showPaypalButtons, async (show) => {
  if (show) {
    paypalButtonRendered.value = false;
    await nextTick();
    renderPayPalButton();
  }
});

// Confirm terms and show PayPal buttons
const confirmTermsAndPay = () => {
  formError.value = '';

  if (!agbAccepted.value && !privacyAccepted.value) {
    formError.value = 'Bitte akzeptieren Sie die AGB und die Datenschutzerklärung, um fortzufahren.';
    return;
  }

  if (!agbAccepted.value) {
    formError.value = 'Bitte akzeptieren Sie die AGB, um fortzufahren.';
    return;
  }

  if (!privacyAccepted.value) {
    formError.value = 'Bitte bestätigen Sie, dass Sie die Datenschutzerklärung gelesen haben.';
    return;
  }

  showPaypalButtons.value = true;
};

// Watch for modal open - skip to payment if already logged in
watch(() => auth.ui.subscriptionVisible, async (visible) => {
  if (visible && auth.isAuthenticated) {
    // User is already logged in - check access and go to payment
    await auth.fetchAccessMe();

    if (auth.hasActiveAccess) {
      // Already has access - show success
      accessEndDate.value = auth.access.grant?.ends_at
        ? parseDate(auth.access.grant.ends_at).toLocaleDateString('de-DE')
        : 'unbegrenzt';
      step.value = 'success';
    } else {
      // Logged in but no access - go directly to payment
      step.value = 'payment';
    }
  }
});

// Close modal
const close = () => {
  auth.hideSubscription();
  resetForm();
};

// Reset form
const resetForm = () => {
  step.value = 'email';
  email.value = '';
  password.value = '';
  firstName.value = '';
  lastName.value = '';
  agbAccepted.value = false;
  privacyAccepted.value = false;
  formError.value = '';
  paypalButtonRendered.value = false;
  justPurchased.value = false;
  showPaypalButtons.value = false;
  selectedAmount.value = 24;
  customAmount.value = '';
  showCustomInput.value = false;
};

// Step 1: Check email
const checkEmail = async () => {
  formError.value = '';

  if (!email.value || !email.value.includes('@')) {
    formError.value = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
    return;
  }

  const result = await auth.checkEmail(email.value);

  if (auth.error) {
    formError.value = auth.error;
    return;
  }

  if (result.exists) {
    step.value = 'login';
  } else {
    step.value = 'register';
  }
};

// Step 2a: Login
const doLogin = async () => {
  formError.value = '';

  if (!password.value) {
    formError.value = 'Bitte geben Sie Ihr Passwort ein.';
    return;
  }

  const ok = await auth.login(email.value, password.value);

  if (!ok) {
    formError.value = auth.error || 'Login fehlgeschlagen';
    return;
  }

  await auth.fetchAccessMe();

  // Check if already has active access
  if (auth.hasActiveAccess) {
    accessEndDate.value = auth.access.grant?.ends_at
      ? parseDate(auth.access.grant.ends_at).toLocaleDateString('de-DE')
      : 'unbegrenzt';
    step.value = 'success';
  } else {
    step.value = 'payment';
  }
};

// Step 2b: Register
const doRegister = async () => {
  formError.value = '';

  if (!firstName.value.trim() || !lastName.value.trim()) {
    formError.value = 'Bitte geben Sie Ihren Vor- und Nachnamen ein.';
    return;
  }

  if (!password.value || password.value.length < 8) {
    formError.value = 'Das Passwort muss mindestens 8 Zeichen lang sein.';
    return;
  }

  const ok = await auth.register(email.value, password.value, firstName.value, lastName.value);

  if (!ok) {
    formError.value = auth.error || 'Registrierung fehlgeschlagen';
    return;
  }

  // Auto-login after registration
  const loginOk = await auth.login(email.value, password.value);

  if (!loginOk) {
    formError.value = 'Registrierung erfolgreich, aber Login fehlgeschlagen. Bitte versuchen Sie es erneut.';
    step.value = 'login';
    return;
  }

  step.value = 'payment';
};

// Go back
const goBack = () => {
  if (step.value === 'login' || step.value === 'register') {
    step.value = 'email';
    password.value = '';
  } else if (step.value === 'payment') {
    close();
  }
};

// Retry after error
const retry = () => {
  step.value = 'payment';
};
</script>

<template>
  <div
    v-if="auth.ui.subscriptionVisible"
    class="fixed inset-0 z-[1000] flex items-center justify-center p-4"
    role="dialog"
    aria-modal="true"
  >
    <!-- Backdrop -->
    <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="close" />

    <!-- Modal -->
    <div class="relative bg-orange-50 w-full max-w-3xl rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="bg-blue text-white px-6 py-4 flex items-center justify-between">
        <h2 class="text-lg font-bold">
          <span v-if="step === 'email'">Zugang zum Bildarchiv</span>
          <span v-else-if="step === 'login'">Anmelden</span>
          <span v-else-if="step === 'register'">Registrieren</span>
          <span v-else-if="step === 'payment'">Bezahlung</span>
          <span v-else-if="step === 'processing'">Verarbeitung...</span>
          <span v-else-if="step === 'success'">Vielen Dank!</span>
          <span v-else-if="step === 'error'">Fehler</span>
        </h2>
        <button class="text-white/80 hover:text-white text-2xl leading-none cursor-pointer" @click="close">&times;</button>
      </div>

      <!-- Content -->
      <div class="p-4 sm:p-5">

        <!-- Step 1: Email -->
        <div v-if="step === 'email'">
          <p class="text-blue mb-4">
            Um vollen Zugang zu unserem Bildarchiv zu erhalten, geben Sie bitte Ihre E-Mail-Adresse ein.
          </p>

          <form @submit.prevent="checkEmail" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-blue mb-1">E-Mail-Adresse</label>
              <input
                v-model="email"
                type="email"
                required
                class="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange"
                placeholder="ihre@email.de"
              />
            </div>

            <p v-if="formError" class="text-error text-sm">{{ formError }}</p>

            <button
              type="submit"
              :disabled="auth.loading"
              class="w-full bg-orange text-white font-bold py-3 px-4 rounded-lg hover:bg-orange/90 disabled:opacity-50"
            >
              {{ auth.loading ? 'Bitte warten...' : 'Weiter' }}
            </button>
          </form>
        </div>

        <!-- Step 2a: Login -->
        <div v-else-if="step === 'login'">
          <p class="text-blue mb-4">
            Willkommen zurück! Bitte geben Sie Ihr Passwort ein.
          </p>

          <form @submit.prevent="doLogin" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-blue mb-1">E-Mail-Adresse</label>
              <input
                :value="email"
                type="email"
                disabled
                class="w-full border border-gray-300 rounded-lg px-4 py-2 bg-gray-100 text-gray-600"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-blue mb-1">Passwort</label>
              <input
                v-model="password"
                type="password"
                required
                class="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange"
                placeholder="Ihr Passwort"
              />
            </div>

            <p v-if="formError" class="text-error text-sm">{{ formError }}</p>

            <div class="flex gap-3">
              <button
                type="button"
                @click="goBack"
                class="flex-1 border border-blue text-blue font-bold py-3 px-4 rounded-lg hover:bg-blue/10"
              >
                Zurück
              </button>
              <button
                type="submit"
                :disabled="auth.loading"
                class="flex-1 bg-orange text-white font-bold py-3 px-4 rounded-lg hover:bg-orange/90 disabled:opacity-50"
              >
                {{ auth.loading ? 'Bitte warten...' : 'Anmelden' }}
              </button>
            </div>
          </form>
        </div>

        <!-- Step 2b: Register -->
        <div v-else-if="step === 'register'">
          <p class="text-blue mb-4">
            Erstellen Sie ein neues Konto, um fortzufahren.
          </p>

          <form @submit.prevent="doRegister" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-blue mb-1">E-Mail-Adresse</label>
              <input
                :value="email"
                type="email"
                disabled
                class="w-full border border-gray-300 rounded-lg px-4 py-2 bg-gray-100 text-gray-600"
              />
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-sm font-medium text-blue mb-1">Vorname</label>
                <input
                  v-model="firstName"
                  type="text"
                  required
                  class="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-blue mb-1">Nachname</label>
                <input
                  v-model="lastName"
                  type="text"
                  required
                  class="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange"
                />
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-blue mb-1">Passwort (min. 8 Zeichen)</label>
              <input
                v-model="password"
                type="password"
                required
                minlength="8"
                class="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange"
                placeholder="Ihr Passwort"
              />
            </div>

            <p v-if="formError" class="text-error text-sm">{{ formError }}</p>

            <div class="flex gap-3">
              <button
                type="button"
                @click="goBack"
                class="flex-1 border border-blue text-blue font-bold py-3 px-4 rounded-lg hover:bg-blue/10"
              >
                Zurück
              </button>
              <button
                type="submit"
                :disabled="auth.loading"
                class="flex-1 bg-orange text-white font-bold py-3 px-4 rounded-lg hover:bg-orange/90 disabled:opacity-50"
              >
                {{ auth.loading ? 'Bitte warten...' : 'Registrieren' }}
              </button>
            </div>
          </form>
        </div>

        <!-- Step 3: Payment -->
        <div v-else-if="step === 'payment'">
          <!-- Compact info for returning users -->
          <p v-if="auth.isAuthenticated" class="text-sm text-blue mb-2">
            Eingeloggt als <strong>{{ auth.email }}</strong>
          </p>

          <!-- Main content in 3-column grid -->
          <div class="bg-white rounded-lg p-3 mb-3 border border-gray-200">
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <!-- Col 1: Amount buttons -->
              <div>
                <p class="text-sm text-gray-700 mb-2 font-medium">Beitrag wählen:</p>
                <div class="grid grid-cols-2 gap-1">
                  <button
                    v-for="amount in PRESET_AMOUNTS"
                    :key="amount"
                    @click="selectAmount(amount)"
                    :class="[
                      'py-2 px-2 rounded font-bold text-sm transition-all border cursor-pointer',
                      selectedAmount === amount && !showCustomInput
                        ? 'bg-orange text-white border-orange'
                        : 'bg-gray-50 text-blue border-gray-200 hover:bg-gray-100'
                    ]"
                  >
                    {{ amount }} €
                  </button>
                </div>
                <button
                  v-if="!showCustomInput"
                  @click="enableCustomAmount"
                  class="text-xs text-orange hover:text-orange/80 underline mt-1 cursor-pointer"
                >
                  Anderen Betrag
                </button>
                <div v-else class="flex items-center gap-1 mt-1">
                  <input
                    v-model="customAmount"
                    type="number"
                    :min="MIN_PRICE"
                    :placeholder="`${MIN_PRICE}`"
                    class="w-16 border border-gray-300 rounded px-2 py-1 text-center text-sm font-bold focus:outline-none focus:ring-1 focus:ring-orange"
                  />
                  <span class="text-blue font-bold text-sm">€</span>
                  <button @click="showCustomInput = false; selectedAmount = 24" class="text-gray-400 hover:text-gray-600 text-sm cursor-pointer">✕</button>
                </div>
              </div>

              <!-- Col 2: Summary -->
              <div class="bg-blue/5 rounded p-2">
                <div class="flex justify-between items-center">
                  <span class="text-gray-600 text-xs">Beitrag</span>
                  <span class="font-bold text-blue text-lg">{{ paymentAmount }} €</span>
                </div>
                <div class="flex justify-between items-center border-t border-blue/10 mt-1 pt-1">
                  <span class="text-gray-600 text-xs">Gültig bis</span>
                  <span class="font-bold text-blue text-sm">{{ computedAccessEndDate }}</span>
                </div>
                <p v-if="paymentAmount > MIN_PRICE" class="text-xs text-green-700 mt-1">❤️ Danke!</p>
              </div>

              <!-- Col 3: Info -->
              <div class="bg-green-50 border border-green-200 rounded p-2 flex items-center">
                <p class="text-green-800 text-xs">
                  <strong>✓ Einmalige Zahlung</strong><br>
                  <span class="text-green-700">Kein Abo, keine automatische Verlängerung.</span>
                </p>
              </div>
            </div>
          </div>

          <!-- Terms and Button in one row on larger screens -->
          <div v-if="!showPaypalButtons">
            <div class="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
              <label class="flex items-center gap-2 cursor-pointer text-sm">
                <input v-model="agbAccepted" type="checkbox" class="w-4 h-4 text-orange border-gray-400 rounded cursor-pointer" />
                <span class="text-blue">Ich akzeptiere die <router-link to="/agb" target="_blank" class="text-orange underline font-medium">AGB</router-link></span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer text-sm">
                <input v-model="privacyAccepted" type="checkbox" class="w-4 h-4 text-orange border-gray-400 rounded cursor-pointer" />
                <span class="text-blue"><router-link to="/page/3/datenschutzerklaerung/" target="_blank" class="text-orange underline font-medium">Datenschutz</router-link> gelesen</span>
              </label>
            </div>

            <p v-if="formError" class="text-red-700 text-sm bg-red-50 border border-red-200 rounded p-2 mb-2">{{ formError }}</p>

            <button
              @click="confirmTermsAndPay"
              class="w-full bg-orange text-white font-bold py-3 px-4 rounded-lg hover:bg-orange/90 text-base shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
              </svg>
              Jetzt sicher bezahlen
            </button>
          </div>

          <!-- Step 2: PayPal payment -->
          <div v-else>
            <div class="flex items-center justify-between gap-3 mb-3">
              <div class="bg-green-50 border border-green-200 rounded px-3 py-2 flex items-center gap-2">
                <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-sm text-green-800 font-medium">AGB akzeptiert</span>
              </div>
              <div class="bg-blue/5 rounded px-3 py-2 text-center">
                <span class="text-blue text-xl font-bold">{{ paymentAmount }} €</span>
              </div>
            </div>

            <p v-if="formError" class="text-red-700 text-sm bg-red-50 border border-red-200 rounded p-2 mb-2">{{ formError }}</p>

            <p class="text-sm text-gray-700 text-center mb-2">Zahlungsart wählen:</p>

            <div id="paypal-button-container" class="min-h-[120px]"></div>

            <button
              @click="showPaypalButtons = false"
              class="w-full text-gray-500 text-sm hover:text-gray-700 py-2 mt-2 cursor-pointer"
            >
              ← Zurück
            </button>
          </div>
        </div>

        <!-- Step: Processing -->
        <div v-else-if="step === 'processing'">
          <div class="text-center py-8">
            <div class="w-16 h-16 border-4 border-orange border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p class="text-blue font-bold text-lg">Zahlung wird verarbeitet...</p>
            <p class="text-gray-600 text-sm mt-2">Bitte warten Sie einen Moment.</p>
          </div>
        </div>

        <!-- Step: Success -->
        <div v-else-if="step === 'success'">
          <div class="text-center py-4">
            <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>

            <h3 class="text-xl font-bold text-blue mb-3">
              {{ justPurchased ? 'Vielen Dank!' : 'Zugang aktiv' }}
            </h3>

            <p class="text-blue text-base mb-4">
              <template v-if="justPurchased">
                Herzlichen Dank für Ihre Unterstützung!<br>
                Sie haben jetzt vollen Zugang zum Bildarchiv.
              </template>
              <template v-else>
                Sie haben bereits vollen Zugang zum Bildarchiv.
              </template>
            </p>

            <div class="bg-green-50 rounded-lg p-4 mb-4 border-2 border-green-200">
              <p class="text-sm text-gray-700">Ihr Zugang ist gültig bis:</p>
              <p class="text-xl font-bold text-green-700 mt-1">{{ accessEndDate }}</p>
            </div>

            <button
              @click="close"
              class="w-full bg-orange text-white font-bold py-3 px-6 rounded-xl hover:bg-orange/90 text-base shadow-lg"
            >
              Zum Bildarchiv
            </button>
          </div>
        </div>

        <!-- Step: Error -->
        <div v-else-if="step === 'error'">
          <div class="text-center py-4">
            <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>

            <h3 class="text-xl font-bold text-blue mb-3">Fehler bei der Zahlung</h3>

            <div class="bg-red-50 border-2 border-red-200 rounded-lg p-3 mb-4">
              <p class="text-red-700 text-base">{{ formError || 'Ein unbekannter Fehler ist aufgetreten.' }}</p>
            </div>

            <p class="text-gray-600 text-sm mb-4">
              Keine Sorge – es wurde kein Geld abgebucht.
            </p>

            <div class="flex gap-3">
              <button
                @click="close"
                class="flex-1 border-2 border-gray-300 text-gray-700 font-bold py-3 px-4 rounded-xl hover:bg-gray-50 text-base transition-all"
              >
                Schließen
              </button>
              <button
                @click="retry"
                class="flex-1 bg-orange text-white font-bold py-3 px-4 rounded-xl hover:bg-orange/90 text-base shadow-lg transition-all"
              >
                Erneut versuchen
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>
